import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography, CircularProgress, TextField, LinearProgress, Box, Select, MenuItem, FormControl, InputLabel, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, List, ListItem, ListItemText } from '@mui/material';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { chunk } from 'lodash';
import './App.css';
import translations from './localizations';
import { GoogleGenerativeAI } from "@google/generative-ai";

function EditPage({ language }) {
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [processingFile, setProcessingFile] = useState(false);
  const [hasEditErrors, setHasEditErrors] = useState(false);
  const [progress, setProgress] = useState(0);
  const [worker, setWorker] = useState(null);
  const BATCH_SIZE = 100;
  const [hasVietnameseTranslation, setHasVietnameseTranslation] = useState(false);
  const [uneditedRows, setUneditedRows] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [geminiApiKey, setGeminiApiKey] = useState(process.env.REACT_APP_GEMINI_API_KEY || '');
  const [systemInstruction, setSystemInstruction] = useState(() => {
    return localStorage.getItem('editSystemInstruction') || 
      `When provided with a set of Korean sentences and their corresponding Vietnamese translations from an Excel file, perform the following steps:
1.	Assess the context: 
-	Examine adjacent sentences in the Excel file to identify common context.
-	Recognize the theme, text style (e.g., journalistic, literary, academic).
2.	Compare and analyze: 
-	Carefully read the Korean sentence and its Vietnamese translation.
-	Evaluate the accuracy and completeness of the translation, considering the identified context.
3.	Identify errors and areas for improvement: 
-	Find grammatical, vocabulary, and syntactical errors in the translation.
-	Pay attention to parts of the translation that are inaccurate, incomplete, or inconsistent with the context.
-	Mark expressions that are unnatural in Vietnamese.
4.	Edit and optimize: 
-	Fix identified errors.
-	Adjust the translation to accurately reflect the meaning and context of the original sentence.
-	Ensure consistency with related sentences in the same paragraph or topic.
5.	Refine the style: 
-	Edit the translation to use natural and appropriate Vietnamese expressions.
-	Adjust sentence structure if necessary to fit Vietnamese writing style.
-	Use words and idioms suitable for the context and style of the original text.
6.	Finalize the translation: 
-	Ensure the final translation is semantically accurate, natural in Vietnamese, and contextually appropriate.
-	Only provide the edited translation without explaining the editing process.
7.	Handle special cases: 
-	If the Korean sentence is meaningless or untranslatable, only provide the number 1 as the answer.
-	If additional information from adjacent sentences is needed for correct understanding, mark for further review.
-	Retain proper nouns as they appear in the original text (e.g., Barbie doll, KFC fried chicken, Michael, Bob).
-	Preserve any special characters present in the original sentence (e.g., 🌷🚗🌟☕).
8.	Finalize: 
-	Ensure the edited result is in pure Vietnamese, except for retained proper nouns and special characters.
-	Provide only the final, refined translation without any explanations of the translation process.
Note: The goal is to create a translation that is faithful to the original content, contextually appropriate, and reads as if it were originally written in Vietnamese, while preserving proper nouns and special characters
`;
  });
  const [openSystemInstruction, setOpenSystemInstruction] = useState(false);
  const [problematicRows, setProblematicRows] = useState([]);
  const [hasEmptyVietnameseTranslations, setHasEmptyVietnameseTranslations] = useState(false);
  const [hasRefinedVietnamese, setHasRefinedVietnamese] = useState(false);
  const [file, setFile] = useState(null);  // Thêm file vào state
  const [currentFile, setCurrentFile] = useState(null);  // Để theo dõi file đang được xử lý
  const [fileStatuses, setFileStatuses] = useState({}); // { fileName: 'pending' | 'processing' | 'completed' | 'error' }
  const [saveDirectory, setSaveDirectory] = useState('');
  const directoryInputRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const [apiErrors, setApiErrors] = useState([]);

  useEffect(() => {
    const newWorker = new Worker(new URL('./editWorker.js', import.meta.url));
    setWorker(newWorker);

    return () => {
      newWorker.terminate();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('editSystemInstruction', systemInstruction);
  }, [systemInstruction]);

  const resetEditState = () => {
    setEdited(false);
    setHasEditErrors(false);
    setProgress(0);
    setProblematicRows([]);
  };

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length === 0) {
      console.log('No files selected');
      return;
    }

    // Reset states
    resetEditState();
    setFiles(newFiles);
    setCurrentFileIndex(0);
    setData(null);
    
    // Khởi tạo trạng thái cho các file mới
    const initialStatuses = {};
    newFiles.forEach(file => {
      initialStatuses[file.name] = 'pending';
    });
    setFileStatuses(initialStatuses);

    // Tự động xử lý file đầu tiên
    const firstFile = newFiles[0];
    setCurrentFile(firstFile);
    setFile(firstFile);
    setProcessingFile(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        await processWorkbook(workbook);
      } catch (error) {
        if (error.message.includes('Password')) {
          setIsPasswordProtected(true);
        } else {
          console.error('Error reading file:', error);
          setFileStatuses(prev => ({
            ...prev,
            [firstFile.name]: 'error'
          }));
        }
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(firstFile);
  };

  const processNextFile = async () => {
    if (currentFileIndex >= files.length) return;
    
    const fileToProcess = files[currentFileIndex];
    setCurrentFile(fileToProcess);
    setFile(fileToProcess);
    setProcessingFile(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        await processWorkbook(workbook);
        setProcessingFile(false);
      } catch (error) {
        if (error.message.includes('Password')) {
          setIsPasswordProtected(true);
        } else {
          console.error('Error reading file:', error);
        }
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(fileToProcess);
  };

  const processWorkbook = (workbook, callback) => {
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(sheet);

    const hasVietnamese = jsonData.some(row => row['Vietnamese'] && row['Vietnamese'].trim() !== '');
    setHasVietnameseTranslation(hasVietnamese);

    const hasEmptyVietnamese = jsonData.some(row => !row['Vietnamese'] || row['Vietnamese'].trim() === '');
    setHasEmptyVietnameseTranslations(hasEmptyVietnamese);

    const hasRefined = jsonData.some(row => row['RefinedVietnamese'] && row['RefinedVietnamese'].trim() !== '');
    setHasRefinedVietnamese(hasRefined);

    if (hasVietnamese) {
      const unedited = jsonData.filter(row => !row['RefinedVietnamese'] || row['RefinedVietnamese'].trim() === '');
      setUneditedRows(unedited);
    }

    setData(jsonData);
    setProcessingFile(false);
    
    if (callback) callback();
  };

  const handlePasswordInput = (event) => {
    setPassword(event.target.value);
  };

  const tryOpenWithPassword = () => {
    setProcessingFile(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary', password: password });
        processWorkbook(workbook);
        setIsPasswordProtected(false);
      } catch (error) {
        console.error('Error reading file with password:', error);
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const editDataInBatches = async () => {
    if (!data || data.length === 0) return;
    if (hasEmptyVietnameseTranslations) {
      alert(t('cannotRefineEmptyTranslations'));
      setFileStatuses(prev => ({
        ...prev,
        [currentFile.name]: 'pending'
      }));
      return;
    }
    if (!saveDirectory) {
      alert(t('pleaseSelectSaveDirectory'));
      setFileStatuses(prev => ({
        ...prev,
        [currentFile.name]: 'pending'
      }));
      return;
    }

    setEditing(true);
    setProgress(0);
    setHasEditErrors(false);
    setApiErrors([]);
    
    // Cập nhật trạng thái file thành 'processing' khi bắt đầu xử lý
    setFileStatuses(prev => ({
      ...prev,
      [currentFile.name]: 'processing'
    }));

    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    const openRouterApiKey = process.env.REACT_APP_OPENROUTER_API_KEY;

    const batches = chunk(data, BATCH_SIZE);
    const totalBatches = batches.length;
    const updatedData = [];
    const problematicRows = [];

    for (let i = 0; i < totalBatches; i++) {
      const startIndex = i * BATCH_SIZE;
      try {
        const result = await new Promise((resolve, reject) => {
          worker.onmessage = (e) => {
            if (e.data.type === 'progress') {
              setProgress(e.data.progress);
            } else if (e.data.type === 'success') {
              resolve(e.data.data);
            } else if (e.data.type === 'error') {
              reject(new Error(e.data.error));
            } else if (e.data.type === 'apiError') {
              setApiErrors(prev => [...prev, e.data.error]);
            }
          };

          worker.postMessage({
            batch: batches[i],
            apiKey,
            geminiApiKey,
            openRouterApiKey,
            columnToEdit: 'Vietnamese',
            startIndex,
            model: selectedModel,
            systemInstruction,
            totalBatches,
            currentBatch: i + 1
          });
        });

        updatedData.push(...result);

        // Check for problematic rows
        result.forEach((row, index) => {
          if (row.EditStatus !== 'OK') {
            problematicRows.push({
              rowNumber: startIndex + index + 1,
              status: row.EditStatus,
              korean: row.Korean,
              vietnamese: row.Vietnamese,
              editedVietnamese: row.EditedVietnamese
            });
          }
        });

      } catch (error) {
        console.error(`Error in batch ${i + 1}:`, error);
        setHasEditErrors(true);
        setApiErrors(prev => [...prev, {
          batch: i + 1,
          error: error.message
        }]);
        handleEditError(error);
      }
    }

    setData(updatedData);
    setProblematicRows(problematicRows);
    setEditing(false);
    setEdited(true);
    setProgress(100);

    try {
      const ws = XLSX.utils.json_to_sheet(updatedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Edited");
      
      // Tạo tên file mới
      const fileName = currentFile.name;
      const fileNameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      const fileExt = fileName.substring(fileName.lastIndexOf('.'));
      const newFileName = `${fileNameWithoutExt}_edited${fileExt}`;

      if (saveDirectory) {
        try {
          // Thử lưu file vào thư mục đã chọn
          const fileHandle = await saveDirectory.getFileHandle(newFileName, { create: true });
          const writable = await fileHandle.createWritable();
          const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
          await writable.write(buffer);
          await writable.close();
        } catch (directoryError) {
          console.warn('Error saving to directory, falling back to regular download:', directoryError);
          // Nếu không thể lưu vào thư mục, tự động tải xuống
          XLSX.writeFile(wb, newFileName);
        }
      } else {
        // Nếu chưa chọn thư mục, tự động tải xuống
        XLSX.writeFile(wb, newFileName);
      }

      // Cập nhật trạng thái của file hiện tại thành 'completed'
      setFileStatuses(prev => ({
        ...prev,
        [currentFile.name]: 'completed'
      }));

      // Move to next file
      const nextIndex = currentFileIndex + 1;
      if (nextIndex < files.length) {
        setCurrentFileIndex(nextIndex);
        const nextFile = files[nextIndex];
        
        // Cập nhật trạng thái của file tiếp theo thành 'processing'
        setFileStatuses(prev => ({
          ...prev,
          [nextFile.name]: 'processing'
        }));

        // Đợi một chút trước khi xử lý file tiếp theo
        setTimeout(() => {
          const nextFile = files[nextIndex];
          setCurrentFile(nextFile);
          setFile(nextFile);
          setProcessingFile(true);
          
          const reader = new FileReader();
          reader.onload = async (e) => {
            try {
              const workbook = XLSX.read(e.target.result, { type: 'binary' });
              await processWorkbook(workbook);
              // Sau khi đọc xong file tiếp theo, tiếp tục hiệu đính
              editDataInBatches();
            } catch (error) {
              console.error('Error reading next file:', error);
              setProcessingFile(false);
            }
          };
          reader.readAsBinaryString(nextFile);
        }, 1000);
      } else {
        // Đã xử lý xong tất cả các file
        setEditing(false);
        setProgress(100);
      }
    } catch (error) {
      console.error('Error saving file:', error);
      // Thử tải xuống file nếu có lỗi
      try {
        const ws = XLSX.utils.json_to_sheet(updatedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Edited");
        const fileName = currentFile.name;
        const fileNameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const fileExt = fileName.substring(fileName.lastIndexOf('.'));
        const newFileName = `${fileNameWithoutExt}_edited${fileExt}`;
        XLSX.writeFile(wb, newFileName);
        
        setFileStatuses(prev => ({
          ...prev,
          [currentFile.name]: 'completed'
        }));
      } catch (downloadError) {
        console.error('Error downloading file:', downloadError);
        alert(t('errorSavingFile'));
        setFileStatuses(prev => ({
          ...prev,
          [currentFile.name]: 'error'
        }));
      }
    }
  };

  const handleEditError = (error) => {
    console.error('Editing error:', error);
    // You can add more error handling logic here
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Edited");
    XLSX.writeFile(wb, "edited_data.xlsx");
  };

  const handleModelChange = (event) => {
    setSelectedModel(event.target.value);
  };

  const t = (key, params = {}) => {
    const translation = translations[language][key];
    if (typeof translation === 'string') {
      return Object.entries(params).reduce(
        (str, [key, value]) => str.replace(`{${key}}`, value),
        translation
      );
    }
    return key;
  };

  const handleReset = () => {
    setFiles([]);
    setCurrentFileIndex(0);
    setFile(null);
    setData(null);
    setEditing(false);
    setEdited(false);
    setIsPasswordProtected(false);
    setPassword('');
    setProcessingFile(false);
    setHasEditErrors(false);
    setProgress(0);
    setHasVietnameseTranslation(false);
    setUneditedRows([]);
    setProblematicRows([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenSystemInstruction = () => {
    setOpenSystemInstruction(true);
  };

  const handleCloseSystemInstruction = () => {
    setOpenSystemInstruction(false);
    localStorage.setItem('editSystemInstruction', systemInstruction);
  };

  // Thêm một hàm để kiểm tra xem có thể hiệu đính hay không
  const canRefine = () => {
    return data && data.length > 0 && !hasEmptyVietnameseTranslations;
  };

  // Sửa lại hàm startEditing
  const startEditing = async () => {
    if (!files.length) return;
    
    setCurrentFileIndex(0);
    const firstFile = files[0];
    setCurrentFile(firstFile);
    setFile(firstFile);
    setProcessingFile(true);
    
    // Cập nhật trạng thái của file đầu tiên thành 'processing'
    setFileStatuses(prev => ({
      ...prev,
      [firstFile.name]: 'processing'
    }));

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        await processWorkbook(workbook);
        // Ngay lập tức bắt đầu hiệu đính
        editDataInBatches();
      } catch (error) {
        if (error.message.includes('Password')) {
          setIsPasswordProtected(true);
        } else {
          console.error('Error reading file:', error);
          // Cập nhật trạng thái thành error nếu có lỗi
          setFileStatuses(prev => ({
            ...prev,
            [firstFile.name]: 'error'
          }));
        }
        setProcessingFile(false);
      }
    };
    reader.readAsBinaryString(firstFile);
  };

  // Thêm hàm xử lý chọn thư mục
  const handleDirectorySelect = async () => {
    try {
      // Sử dụng showDirectoryPicker API để chọn thư mục
      const dirHandle = await window.showDirectoryPicker();
      setSaveDirectory(dirHandle);
    } catch (err) {
      // Nếu người dùng hủy việc chọn thư mục, không hiển thị lỗi trong console
      if (err.name === 'AbortError') {
        return; // Người dùng đã hủy việc chọn thư mục, không làm gì cả
      }
      // Nếu là lỗi khác, hiển thị trong console và thông báo cho người dùng
      console.error('Error selecting directory:', err);
      alert(t('errorSelectingDirectory'));
    }
  };

  return (
    <Box>
      <Box className="language-selector">
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenSystemInstruction}
        >
          System instruction
        </Button>

        <FormControl variant="outlined" size="small">
          <InputLabel id="model-select-label">{t('model')}</InputLabel>
          <Select
            labelId="model-select-label"
            id="model-select"
            value={selectedModel}
            onChange={handleModelChange}
            label={t('model')}
          >
            <MenuItem value="gpt-4o">GPT-4o</MenuItem>
            <MenuItem value="gpt-4o-mini">GPT-4o-mini</MenuItem>
            {/* <MenuItem value="openai/gpt-4o">GPT-4o</MenuItem> */}
            {/* <MenuItem value="openai/gpt-4o-mini">GPT-4o-mini</MenuItem> */}
            {/* <MenuItem value="nvidia/llama-3.1-nemotron-70b-instruct">Nvidia: Llama 3.1 Nemotron 70B Instruct</MenuItem> */}
            {/* <MenuItem value="google/gemini-flash-1.5">Google: Gemini Flash 1.5</MenuItem> */}
            <MenuItem value="google/gemini-2.0-flash-001">Google: Gemini Flash 2.0</MenuItem>
            <MenuItem value="google/gemini-pro-1.5">Google: Gemini Pro 1.5</MenuItem>
            <MenuItem value="deepseek/deepseek-r1">DeepSeek: R1</MenuItem>
            <MenuItem value="deepseek/deepseek-r1-distill-llama-70b">DeepSeek: R1 Distill Llama 70B</MenuItem>
            <MenuItem value="anthropic/claude-3.5-sonnet">Anthropic: Claude 3.5 Sonnet</MenuItem>
            <MenuItem value="eva-unit-01/eva-qwen-2.5-72b">Qwen2.5 72B</MenuItem>
            {/* <MenuItem value="meta-llama/llama-3.1-70b-instruct">Meta: Llama 3.1 70B Instruct</MenuItem> */}
            {/* <MenuItem value="meta-llama/llama-3.2-90b-vision-instruct">Meta: Llama 3.2 90B Vision Instruct</MenuItem> */}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '100px' }}>
        {t('editTitle')}
      </Typography>
      <Box className="center-content">
        <div className="button-container">
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="file-upload"
            ref={fileInputRef}
            multiple
          />
          <label htmlFor="file-upload">
            <Button variant="contained" component="span" disabled={processingFile} className="custom-button">
              {processingFile ? <CircularProgress size={24} /> : t('uploadButton')}
            </Button>
          </label>
          {files.length > 0 && (
            <>
              <Button
                variant="contained"
                onClick={handleDirectorySelect}
                className="custom-button"
                color="primary"
              >
                {saveDirectory ? t('changeSaveDirectory') : t('selectSaveDirectory')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleReset}
                className="reset-button custom-button"
              >
                {t('resetButton')}
              </Button>
            </>
          )}
        </div>
        {saveDirectory && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
            {t('selectedDirectory')}: {saveDirectory.name}
          </Typography>
        )}
        {files.length > 0 && !processingFile && (
          <Typography variant="body1" gutterBottom align="center">
            {hasRefinedVietnamese && (
              <Typography color="primary" align="center">
                {t('hasEditedTranslations')}
              </Typography>
            )}
            {hasEmptyVietnameseTranslations && (
              <Typography color="error" align="center">
                {t('hasEmptyVietnameseTranslations')}
              </Typography>
            )}
            {hasVietnameseTranslation && uneditedRows.length > 0 && (
              <Typography color="error" align="center">
                {t('uneditedRows', { count: uneditedRows.length })}
              </Typography>
            )}
          </Typography>
        )}
      </Box>
      {isPasswordProtected && (
        <Box mt={2} display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} justifyContent="center">
          <Typography variant="body1" color="error" gutterBottom align="center">
            {t('passwordProtected')}
          </Typography>
          <TextField
            type="password"
            label={t('passwordLabel')}
            variant="outlined"
            value={password}
            onChange={handlePasswordInput}
            disabled={processingFile}
            fullWidth={isMobile}
            style={isMobile ? { marginBottom: '1rem' } : { marginRight: '1rem' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={tryOpenWithPassword}
            disabled={processingFile}
            fullWidth={isMobile}
          >
            {processingFile ? <CircularProgress size={24} /> : t('openFileButton')}
          </Button>
        </Box>
      )}
      {files.length > 0 && (
        <Box className="center-content">
          <Button
            variant="contained"
            color="primary"
            onClick={editDataInBatches}
            disabled={editing || processingFile}
            style={{ marginTop: '20px' }}
          >
            {editing ? <CircularProgress size={24} /> : t('editButton')}
          </Button>
        </Box>
      )}
      {editing && (
        <div style={{ marginTop: '20px' }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="textSecondary">
            {`${Math.round(progress)}%`}
          </Typography>
        </div>
      )}
      
      <Dialog open={openSystemInstruction} onClose={handleCloseSystemInstruction} maxWidth="md" fullWidth>
        <DialogTitle>System Instruction</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={10}
            fullWidth
            variant="outlined"
            value={systemInstruction}
            onChange={(e) => setSystemInstruction(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSystemInstruction} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {files.length > 0 && (
        <Box width="100%" maxWidth={600} mx="auto" mb={4} mt={4}>
          <Typography variant="h6" gutterBottom>
            {t('selectedFiles')}
          </Typography>
          <Paper 
            sx={{ 
              maxHeight: 200, 
              overflow: 'auto',
              marginBottom: 2,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '4px',
                '&:hover': {
                  background: '#555',
                },
              },
            }}
          >
            <List>
              {files.map((file, index) => (
                <ListItem key={index}>
                  <ListItemText 
                    primary={`${index + 1}. ${file.name}`}
                    secondary={
                      <Typography 
                        component="span" 
                        sx={{ 
                          color: fileStatuses[file.name] === 'completed' ? 'success.main' : 
                                 fileStatuses[file.name] === 'error' ? 'error.main' :
                                 fileStatuses[file.name] === 'processing' ? 'info.main' : 
                                 'text.secondary'
                        }}
                      >
                        {fileStatuses[file.name] === 'completed' ? t('statusCompleted') :
                         fileStatuses[file.name] === 'error' ? t('statusError') :
                         fileStatuses[file.name] === 'processing' ? t('statusProcessing') :
                         t('statusPending')}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Box>
      )}

      {edited && (
        <Box mt={4}>
          {apiErrors.length > 0 && (
            <Box mb={4}>
              <Typography variant="h6" gutterBottom color="error">
                {t('apiErrors')}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('batchNumber')}</TableCell>
                      <TableCell>{t('errorMessage')}</TableCell>
                      <TableCell>{t('errorCode')}</TableCell>
                      <TableCell>{t('errorType')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiErrors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.batch || 'N/A'}</TableCell>
                        <TableCell>{error.message || error.error}</TableCell>
                        <TableCell>{error.code || 'N/A'}</TableCell>
                        <TableCell>{error.type || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Typography variant="h6" gutterBottom>
            {t('problematicRows')}
          </Typography>
          {problematicRows.filter(row => row.status === 'Error').length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Row Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Korean</TableCell>
                    <TableCell>Vietnamese</TableCell>
                    <TableCell>Refined Vietnamese</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {problematicRows
                    .filter(row => row.status === 'Error')
                    .map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.korean}</TableCell>
                        <TableCell>{row.vietnamese}</TableCell>
                        <TableCell>{row.editedVietnamese}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>{t('noProblematicRowsFound')}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default EditPage;
